import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import { motion } from 'framer-motion';
import {
  FiAward,
  FiBarChart2,
  FiGrid,
  FiHeart,
  FiHelpCircle,
  FiInfo,
  FiZap,
} from 'react-icons/fi';
import {
  FaFacebook,
  FaInstagram,
} from 'react-icons/fa';
import { FaThreads } from 'react-icons/fa6';
import { useGame } from '../contexts/GameContext';
import { useLanguage } from '../contexts/LanguageContext';
import MokugyoContainer from '../components/game/MokugyoContainer';
import GameControls from '../components/game/GameControls';
import LanguageSwitcher from '../components/ui/LanguageSwitcher';
import MokugyoIcon from '../components/ui/MokugyoIcon';
import TypeIcon from '../components/ui/TypeIcon';
import StatIcon from '../components/ui/StatIcon';
import SafeIcon from '../components/common/SafeIcon';
import Modal from '../components/ui/Modal';
import AchievementsModal from '../components/modals/AchievementsModal';
import SpecialFishModal from '../components/modals/SpecialFishModal';
import HelpModal from '../components/modals/HelpModal';
import DonationModal from '../components/modals/DonationModal';
import TypeStatsModal from '../components/modals/TypeStatsModal';
import TypeSelectorModal from '../components/modals/TypeSelectorModal';
import AboutModal from '../components/modals/AboutModal';
import { SPECIAL_FISH_TYPES } from '../constants/specialFish';
import { showNotification } from '../components/ui/NotificationContainer';

const SOCIAL_LINKS = [
  {
    href: 'https://www.threads.com/@homy.hk',
    icon: FaThreads,
    label: 'Threads',
  },
  {
    href: 'https://www.instagram.com/homy.hk',
    icon: FaInstagram,
    label: 'Instagram',
  },
  {
    href: 'https://www.facebook.com/profile.php?id=61578745554661',
    icon: FaFacebook,
    label: 'Facebook',
  },
];

const HomePage = () => {
  const {
    state,
    toggleAutoTap,
    dispatch,
  } = useGame();
  const { t } = useLanguage();
  const [activeModal, setActiveModal] = useState(null);

  const handleOpenModal = useCallback((modalName) => {
    setActiveModal(modalName);

    if (
      modalName === 'achievements'
      && state.unviewedAchievements.length > 0
    ) {
      dispatch({ type: 'MARK_ACHIEVEMENTS_VIEWED' });
    }

    if (
      modalName === 'fish'
      && state.unviewedFish.length > 0
    ) {
      dispatch({ type: 'MARK_FISH_VIEWED' });
    }
  }, [
    dispatch,
    state.unviewedAchievements,
    state.unviewedFish,
  ]);

  const handleCloseModal = useCallback(() => {
    setActiveModal(null);
  }, []);

  const handleFishCardClick = useCallback(() => {
    showNotification(
      t('game.clickToChangeFish') || '點擊下方按鈕可切換木魚',
      'info',
      2000,
    );

    setTimeout(() => {
      handleOpenModal('fish');
    }, 300);
  }, [
    handleOpenModal,
    t,
  ]);

  const hasUnviewedAchievements = useMemo(
    () => state.unviewedAchievements?.length > 0,
    [state.unviewedAchievements],
  );

  const hasUnviewedFish = useMemo(
    () => state.unviewedFish?.length > 0,
    [state.unviewedFish],
  );

  const renderFishIcon = useCallback((fishId, size = 32) => {
    const fish = SPECIAL_FISH_TYPES.find(
      (item) => item.id === fishId,
    );

    if (!fish) {
      return <MokugyoIcon type="normal" size={size} />;
    }

    if (fish.useSvg) {
      return (
        <MokugyoIcon
          type={fish.svgType || 'normal'}
          size={size}
        />
      );
    }

    return (
      <span
        style={{
          fontSize: `${size}px`,
          lineHeight: 1,
        }}
      >
        {fish.emoji}
      </span>
    );
  }, []);

  return (
    <div className="game-screen">
      <header className="game-header">
        <div className="header-spacer" />

        <div className="stats-container">
          <div className="stat-badge">
            <div className="stat-icon">
              <StatIcon type="total" size={24} />
            </div>
            <div>
              <div className="stat-label">
                {t('game.stats.total')}
              </div>
              <div className="stat-value">
                {state.totalTaps.toLocaleString()}
              </div>
            </div>
          </div>

          <div className="stat-badge">
            <div className="stat-icon">
              <StatIcon type="combo" size={24} />
            </div>
            <div>
              <div className="stat-label">
                {t('game.stats.combo')}
              </div>
              <div className="stat-value">
                {state.combo}x
              </div>
            </div>
          </div>

          <div className="stat-badge">
            <div className="stat-icon">
              <StatIcon type="record" size={24} />
            </div>
            <div>
              <div className="stat-label">
                {t('game.stats.record')}
              </div>
              <div className="stat-value">
                {state.maxCombo}
              </div>
            </div>
          </div>
        </div>

        <div className="header-actions">
          <motion.button
            className="donation-header-btn"
            onClick={() => handleOpenModal('donation')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title={t('donation.title')}
          >
            <SafeIcon
              icon={FiHeart}
              className="text-lg"
            />
          </motion.button>

          <LanguageSwitcher />
        </div>
      </header>

      <main className="game-main">
        <div className="game-sidebar left">
          <button
            className="sidebar-btn"
            onClick={() => handleOpenModal('achievements')}
            title={t('achievements.title')}
          >
            <SafeIcon icon={FiAward} />
            {hasUnviewedAchievements && (
              <span className="sidebar-notification-badge">
                !
              </span>
            )}
          </button>

          <button
            className="sidebar-btn"
            onClick={() => handleOpenModal('fish')}
            title={t('specialFish.title')}
          >
            <SafeIcon icon={FiGrid} />
            {hasUnviewedFish && (
              <span className="sidebar-notification-badge">
                !
              </span>
            )}
          </button>

          <button
            className="sidebar-btn"
            onClick={() => handleOpenModal('typeStats')}
            title={t('typeStats.title')}
          >
            <SafeIcon icon={FiBarChart2} />
          </button>

          <button
            className="sidebar-btn"
            onClick={() => handleOpenModal('help')}
            title={t('help.title')}
          >
            <SafeIcon icon={FiHelpCircle} />
          </button>
        </div>

        <div className="game-center">
          <MokugyoContainer />
        </div>

        <div className="game-sidebar right">
          <motion.button
            className="mini-card mini-card-clickable"
            onClick={() => handleOpenModal('typeSelector')}
            title={
              t('game.clickToChangeType')
              || t('game.currentType')
            }
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="mini-card-title">
              {t('game.currentType')}
            </div>
            <div className="mini-card-icon">
              <TypeIcon
                type={state.currentType}
                size={32}
              />
            </div>
          </motion.button>

          <motion.button
            className="mini-card mini-card-clickable"
            onClick={handleFishCardClick}
            title={
              t('game.clickToChangeFish')
              || t('specialFish.title')
            }
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="mini-card-title">
              {t('game.inUse')}
            </div>
            <div className="mini-card-icon">
              {renderFishIcon(
                state.currentSpecialFish,
                32,
              )}
            </div>
          </motion.button>

          <button
            className={`mini-card auto-tap ${
              state.autoTapActive ? 'active' : ''
            }`}
            onClick={toggleAutoTap}
          >
            <SafeIcon
              icon={FiZap}
              className="text-2xl"
            />
            <div className="mini-card-title">
              {state.autoTapActive
                ? t('game.on')
                : t('game.off')}
            </div>
          </button>
        </div>
      </main>

      <footer className="game-footer">
        <GameControls />

        <div className="footer-bottom-row">
          <motion.a
            href="https://homy.hk"
            target="_blank"
            rel="noopener noreferrer"
            className="brand-link"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            whileHover={{ scale: 1.02 }}
          >
            <span className="brand-zen">
              {t('brand.tagline')}
            </span>
            <span className="brand-by">
              BY
            </span>
            <span className="brand-name">
              {t('brand.company')}
            </span>
          </motion.a>

          <div className="social-links">
            {SOCIAL_LINKS.map((social) => (
              <motion.a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                title={social.label}
                aria-label={social.label}
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.9 }}
              >
                <SafeIcon icon={social.icon} />
              </motion.a>
            ))}

            <motion.button
              type="button"
              className="social-link footer-about-button"
              title="關於我們與聯絡方式"
              aria-label="關於我們與聯絡方式"
              onClick={() => handleOpenModal('about')}
              whileHover={{ scale: 1.15 }}
              whileTap={{ scale: 0.9 }}
            >
              <SafeIcon icon={FiInfo} />
            </motion.button>
          </div>
        </div>
      </footer>

      <div className="mobile-quick-actions">
        <button
          className={`quick-btn ${
            state.autoTapActive ? 'active-auto' : ''
          }`}
          onClick={toggleAutoTap}
          title={t('game.autoTap')}
        >
          <SafeIcon icon={FiZap} />
        </button>

        <button
          className="quick-btn"
          onClick={() => handleOpenModal('achievements')}
        >
          <SafeIcon icon={FiAward} />
          {hasUnviewedAchievements && (
            <span className="quick-notification-badge">
              !
            </span>
          )}
        </button>

        <button
          className="quick-btn"
          onClick={() => handleOpenModal('fish')}
        >
          <SafeIcon icon={FiGrid} />
          {hasUnviewedFish && (
            <span className="quick-notification-badge">
              !
            </span>
          )}
        </button>

        <button
          className="quick-btn"
          onClick={() => handleOpenModal('typeStats')}
        >
          <SafeIcon icon={FiBarChart2} />
        </button>

        <button
          className="quick-btn"
          onClick={() => handleOpenModal('help')}
        >
          <SafeIcon icon={FiHelpCircle} />
        </button>
      </div>

      <Modal
        isOpen={activeModal === 'achievements'}
        onClose={handleCloseModal}
      >
        <AchievementsModal onClose={handleCloseModal} />
      </Modal>

      <Modal
        isOpen={activeModal === 'fish'}
        onClose={handleCloseModal}
        showContentDate
      >
        <SpecialFishModal onClose={handleCloseModal} />
      </Modal>

      <Modal
        isOpen={activeModal === 'help'}
        onClose={handleCloseModal}
        showContentDate
      >
        <HelpModal onClose={handleCloseModal} />
      </Modal>

      <Modal
        isOpen={activeModal === 'donation'}
        onClose={handleCloseModal}
      >
        <DonationModal onClose={handleCloseModal} />
      </Modal>

      <Modal
        isOpen={activeModal === 'typeStats'}
        onClose={handleCloseModal}
        showContentDate
      >
        <TypeStatsModal onClose={handleCloseModal} />
      </Modal>

      <Modal
        isOpen={activeModal === 'typeSelector'}
        onClose={handleCloseModal}
      >
        <TypeSelectorModal onClose={handleCloseModal} />
      </Modal>

      <Modal
        isOpen={activeModal === 'about'}
        onClose={handleCloseModal}
        showContentDate
      >
        <AboutModal onClose={handleCloseModal} />
      </Modal>
    </div>
  );
};

export default HomePage;